import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PageTitle } from 'ui/PageTitle';
import { Table } from 'ui/Table';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { usePagination } from 'hooks/usePagination';
import { ESortOrder } from 'store/common/types';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import {
  useOpportunityList,
  useCategories,
  useDeleteOpportunity,
} from 'containers/ContentMarketing/hooks/useContentMarketingQueries';
import { formatDateDisplay } from 'utils/date';
import { CircleIconButton } from 'ui/CircleIconButton';
import { HidingTooltip } from 'ui/Tooltip/HidingTooltip';
import { makeBackendApi } from 'services/BackendApi';
import PencilIcon from 'ui/Icons/pencil-no-circle.component.svg';
import TrashIcon from 'ui/Icons/tabler/trash.component.svg';
import ChevronRightIcon from 'ui/Icons/tabler/chevron-right.component.svg';
import { IValueLabelPair } from 'interfaces';
import { TextInput } from 'ui/TextInput';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import FluidButton from 'ui/FluidButton';
import { FormLabel } from 'ui/FormLabel';
import DateRangeInput from 'pureUi/DateRangeInput';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { ExistingDirectoryEntriesSimpleSearch } from 'containers/CRM/components/ExistingDirectoryEntriesSearch/ExistingDirectoryEntriesSimpleSearch';
import { ECrmDirectoryEntryType } from 'services/CrmApi/types/DirectoryEntry';
import { useDebounce } from 'hooks/useDebounce';
import { parseAsUTC, generateArrayOfDatesBetween } from 'utils/date.utc';
import { useModal } from 'hooks/useModal';
import { ConfirmationModal, EConfirmationModalType } from 'ui/ConfirmationModal';
import { useNotifications } from 'hooks/useNotifications';
import classNames from 'classnames';
import { SvgIcon } from 'ui/SvgIcon';
import CircleIcon from 'ui/Icons/tabler/circle.component.svg';

// Filter interface
export interface IContentFilters {
  title: string;
  crmCompanyUuid: string | null;
  categoryUuids: string[];
  destinations: string[];
  status: 'Draft' | 'Active' | 'Scheduled' | 'Expired' | null;
  publishDateFrom: string | null;
  publishDateTo: string | null;
}

// Status options with colored circle icons (matching StatusIndicator in Content.tsx)
const STATUS_OPTIONS: IMultiselectValueLabelPair[] = [
  {
    value: 'Draft',
    label: 'Draft',
    iconHtml: <SvgIcon IconComponent={CircleIcon} className="w-3 h-3 fill-bright-sky-blue" />,
    iconAlign: 'end',
  },
  {
    value: 'Active',
    label: 'Active',
    iconHtml: <SvgIcon IconComponent={CircleIcon} className="w-3 h-3 fill-status-confirmed" />,
    iconAlign: 'end',
  },
  {
    value: 'Scheduled',
    label: 'Scheduled',
    iconHtml: <SvgIcon IconComponent={CircleIcon} className="w-3 h-3 fill-status-requested" />,
    iconAlign: 'end',
  },
  {
    value: 'Expired',
    label: 'Expired',
    iconHtml: <SvgIcon IconComponent={CircleIcon} className="w-3 h-3 fill-status-discarded" />,
    iconAlign: 'end',
  },
];

// Category badge component
const CategoryBadge = ({ category }: { category: string }) => (
  <span style={{ width: 'max-content' }} className="inline-flex items-center bg-gray-10 rounded px-5px py-5px">
    <p className="text-13px font-pt-sans tracking-wider text-text-colour m-0 p-0">{category}</p>
  </span>
);

// Status indicator component with colored dots
const StatusIndicator = ({ status }: { status: string }) => {
  const colorMap = {
    Draft: 'bg-bright-sky-blue',
    Active: 'bg-status-confirmed',
    Scheduled: 'bg-status-requested',
    Expired: 'bg-status-discarded',
  };

  return (
    <span className="flex items-center justify-between w-full">
      <span className="italic">{status}</span>
      <span className={`w-3 h-3 rounded-full ${colorMap[status] || 'bg-gray-40'}`} />
    </span>
  );
};

// Table columns configuration
const tableColumns: ITableColumn[] = [
  { id: 'title', label: 'Title', width: 'auto', fieldToSortBy: 'title' },
  { id: 'crmCompany', label: 'CRM Company', width: 'auto' }, // Not sortable - API missing company name
  { id: 'category', label: 'Category', width: '200px', fieldToSortBy: 'category' },
  { id: 'destination', label: 'Destination', width: '150px', fieldToSortBy: 'destination' },
  { id: 'publishDate', label: 'Publish Date', width: '150px', fieldToSortBy: 'activeFrom' },
  { id: 'status', label: 'Status', width: '120px' }, // Not sortable - API doesn't support
  { id: 'actions', label: '', width: '180px' }, // Not sortable
];

export const Content = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const deleteModal = useModal();
  const [itemToDelete, setItemToDelete] = React.useState<string | null>(null);

  // Pagination state (1-indexed to match API)
  const paginationData = usePagination({
    initialPage: 1,
    initialPerPage: 10,
    initialTotal: 0,
  });

  // Sort state (default: newest content first)
  const [sortBy, setSortBy] = useState('creationDate');
  const [sortOrder, setSortOrder] = useState<ESortOrder>(ESortOrder.DESC);

  // Filter state - single object
  const [filtersState, setFiltersState] = useState<IContentFilters>({
    title: '',
    crmCompanyUuid: null,
    categoryUuids: [],
    destinations: [],
    status: null,
    publishDateFrom: null,
    publishDateTo: null,
  });

  // Wrapper function that spreads updates and resets pagination
  const setFilters = (updates: Partial<IContentFilters>) => {
    setFiltersState(prev => ({ ...prev, ...updates }));
    paginationData.setPage(1);
  };

  // Local title state with debouncing
  const [localTitle, setLocalTitle, debouncedLocalTitle] = useDebounce('', 1000);

  useEffect(() => {
    if (debouncedLocalTitle.length === 0) {
      setFilters({ title: '' });
    }
    if (debouncedLocalTitle.length >= 3 && debouncedLocalTitle !== filtersState.title) {
      setFilters({ title: debouncedLocalTitle });
    }
  }, [debouncedLocalTitle]);

  // Load categories for filter
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  const categoryOptions: IValueLabelPair[] = (categoriesData || []).map(category => ({
    value: category.uuid,
    label: category.value,
  }));

  // Load destinations for filter
  const [destinationOptions, setDestinationOptions] = useState<IValueLabelPair[]>([]);
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(true);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const backendApi = makeBackendApi();
        const response = await backendApi.getNamesSearch('');
        const data = response.data.data;

        const options: IValueLabelPair[] = [];
        if (data.countries) {
          data.countries.forEach(country => {
            options.push({
              value: country.name,
              label: country.name,
            });
          });
        }

        setDestinationOptions(options);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setIsLoadingDestinations(false);
      }
    };

    loadCountries();
  }, []);

  // Reset filters handler
  const handleResetFilters = () => {
    setFiltersState({
      title: '',
      crmCompanyUuid: null,
      categoryUuids: [],
      destinations: [],
      status: null,
      publishDateFrom: null,
      publishDateTo: null,
    });
    setLocalTitle('');
    paginationData.setPage(1);
  };

  // Helper to show validation message for title filter
  const showTitleValidation = localTitle.length > 0 && localTitle.length < 3;

  // Selected dates for date picker
  const selectedDates: string[] =
    filtersState.publishDateFrom && filtersState.publishDateTo
      ? generateArrayOfDatesBetween(filtersState.publishDateFrom, filtersState.publishDateTo)
      : [];

  // Fetch opportunities list with filters
  const { data, isLoading, isError, isFetching } = useOpportunityList(
    paginationData.page,
    paginationData.perPage,
    sortBy,
    sortOrder,
    {
      title: filtersState.title || undefined,
      crmCompany: filtersState.crmCompanyUuid || undefined, // Map crmCompanyUuid to API's crmCompany param
      categoryUuids: filtersState.categoryUuids.length > 0 ? filtersState.categoryUuids : undefined,
      destinations: filtersState.destinations.length > 0 ? filtersState.destinations : undefined,
      status: filtersState.status || undefined,
      publishDateFrom: filtersState.publishDateFrom || undefined,
      publishDateTo: filtersState.publishDateTo || undefined,
    }
  );

  // Update total when data loads
  useEffect(() => {
    if (data?.total !== undefined) {
      paginationData.setTotal(data.total);
    }
  }, [data]);

  // REQUIRED: Cleanup queries on unmount
  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: ['marketing-manager', 'opportunities'] });
    };
  }, [queryClient]);

  // Handle sorting
  const handleSort = (newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder as ESortOrder);
  };

  // Handle create content button
  const handleCreateContent = () => {
    history.push('/content/create');
  };

  // Delete mutation
  const deleteOpportunityMutation = useDeleteOpportunity();

  // Handle delete click
  const handleDeleteClick = (uuid: string) => {
    setItemToDelete(uuid);
    deleteModal.showModal();
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await deleteOpportunityMutation.mutateAsync(itemToDelete);
      showSuccessNotification('Content deleted successfully');
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['marketing-manager', 'opportunities'] });
      deleteModal.handleClose();
      setItemToDelete(null);
    } catch (error) {
      showErrorNotification('Failed to delete content');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    deleteModal.handleClose();
    setItemToDelete(null);
  };

  // Transform API data to table rows
  const items = data?.items || [];
  const tableRows: ITableRow[] = items.map(item => ({
    id: item.uuid,
    cells: [
      { id: 'title', value: item.title },
      { id: 'crmCompany', value: item.crmCompanyName || '-' },
      {
        id: 'category',
        value:
          item.categories && item.categories.length > 0 ? (
            <span
              className={classNames('flex flex-col gap-1', {
                'my-1': item.categories.length > 1,
              })}
            >
              {item.categories.map((cat, idx) => (
                <CategoryBadge key={idx} category={cat.value || ''} />
              ))}
            </span>
          ) : (
            '-'
          ),
      },
      {
        id: 'destination',
        value: item.destinations && item.destinations.length > 0 ? item.destinations.join(', ') : '',
      },
      {
        id: 'publishDate',
        value: item.activeFrom ? formatDateDisplay(item.activeFrom) : '',
      },
      { id: 'status', value: <StatusIndicator status={item.status} /> },
      {
        id: 'actions',
        value: (
          <div className="flex items-center gap-15px ml-5px">
            <div className="w-4px h-30px rounded bg-gray-10" />
            <HidingTooltip position="top" renderTooltipContent={() => <span className="p-5px">Go to Details</span>}>
              <CircleIconButton
                type="secondary"
                IconComponent={ChevronRightIcon}
                className="ml-5px"
                onClick={() => window.open(`/content/${item.uuid}`, '_blank')}
              />
            </HidingTooltip>
            <HidingTooltip position="top" renderTooltipContent={() => <span className="p-5px">Edit</span>}>
              <CircleIconButton
                type="secondary"
                IconComponent={PencilIcon}
                className="fill-brown-prime"
                onClick={() => history.push(`/content/edit/${item.uuid}`)}
              />
            </HidingTooltip>
            <HidingTooltip position="top" renderTooltipContent={() => <span className="p-5px">Remove</span>}>
              <CircleIconButton
                type="secondary"
                IconComponent={TrashIcon}
                onClick={() => handleDeleteClick(item.uuid)}
              />
            </HidingTooltip>
          </div>
        ),
      },
    ],
  }));

  // Determine if we have initial data
  const hasInitialData = Boolean(data);
  const total = data?.total || 0;

  // Results count text
  const getResultsText = (): string => {
    if (total === 0) return 'no results found';
    if (total === 1) return '1 result found';
    return `${total} results found`;
  };

  return (
    <div className="marketing-content-listing w-auto mx-[20px] crm-desktop:mx-[80px]">
      <PageTitle
        primaryText="Content"
        secondaryCondition={hasInitialData && !isLoading}
        secondaryText={`(${getResultsText()})`}
        loadingCondition={isFetching}
        loadingText="Loading ..."
      />

      {/* Filters */}
      <div className="w-full flex flex-col gap-15px mb-20px">
        {/* Title */}
        <span className="font-pt-sans font-bold text-17px text-text-colour">Filter Results</span>

        {/* Row 1 - Main filters */}
        <div className="flex items-stretch gap-20px w-full">
          {/* Search Content */}
          <div className="flex-1 flex flex-col">
            <FormLabel text="Search Content" className="mb-5px text-13px tracking-wider">
              <TextInput
                id="filter-title"
                value={localTitle}
                onChange={e => {
                  if (e.currentTarget.value.length === 0) {
                    setFilters({ title: '' });
                  }
                  setLocalTitle(e.currentTarget.value);
                }}
                placeholder="Search by title..."
                inputClassName="h-35px mt-0"
              />
            </FormLabel>
          </div>

          {/* CRM Company */}
          <div className="flex-1 flex flex-col">
            <span className="font-pt-sans block mb-5px text-13px tracking-wider text-black">CRM Company</span>
            <ExistingDirectoryEntriesSimpleSearch
              // key={`crm-company-${filtersState.crmCompanyUuid}`}
              value={filtersState.crmCompanyUuid || ''}
              onChange={value => setFilters({ crmCompanyUuid: value })}
              directoryEntryType={ECrmDirectoryEntryType.COMPANY}
              className="h-35px"
            />
            {/* <FormLabel text="CRM Company" className="mb-5px text-13px tracking-wider"></FormLabel> */}
          </div>

          {/* Category */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* <FormLabel text="Category" className="mb-5px text-13px tracking-wider">
            </FormLabel> */}
            <span className="font-pt-sans block mb-5px text-13px tracking-wider text-black">Category</span>
            <Multiselect
              options={categoryOptions}
              selectedValues={filtersState.categoryUuids}
              onUpdate={values => setFilters({ categoryUuids: values })}
              placeholderText="All"
              primaryCtaClassName="bg-ivory"
              itemsClassname="bg-white"
              itemCtaClassName="hover:bg-gray-10"
              disabled={isLoadingCategories}
            />
          </div>

          {/* Destination */}
          <div className="flex-1 flex flex-col min-w-0">
            <FormLabel text="Destination" className="mb-5px text-13px tracking-wider">
              <Multiselect
                options={destinationOptions}
                selectedValues={filtersState.destinations}
                onUpdate={values => setFilters({ destinations: values })}
                placeholderText="All"
                primaryCtaClassName="bg-ivory"
                itemsClassname="bg-white"
                itemCtaClassName="hover:bg-gray-10"
                disabled={isLoadingDestinations}
              />
            </FormLabel>
          </div>
        </div>

        {/* Row 2 - Date and Status */}
        <div className="flex items-stretch gap-20px w-full">
          {/* Publish Date */}
          <div className="flex-1 flex flex-col max-w-[180px]">
            <FormLabel text="Publish Date" className="mb-5px text-13px tracking-wider">
              <DatePickerStateProvider
                defaultSelectedDates={selectedDates}
                onDateChange={dates => {
                  const publishDateFrom = parseAsUTC(new Date(dates[0]).toISOString());
                  const publishDateTo = parseAsUTC(new Date(dates[dates.length - 1]).toISOString());
                  setFilters({ publishDateFrom, publishDateTo });
                }}
                render={(params: IDatePickerSateParams) => {
                  return (
                    <DateRangeInput
                      className="serachBarDateRangeInput"
                      displayString={params.displayString}
                      currentDate={params.datePickerCurrentDate}
                      totalNights={params.totalNights}
                      selectedDates={params.selectedDates}
                      onDayClick={params.handleDayClick}
                      onDayMouseOver={params.handleDateMouseOver}
                      showDatePicker={params.showDatePicker}
                      onNextClick={params.incrementDate}
                      onPrevClick={params.decrementDate}
                      onClick={params.toggleDatePicker}
                      onClickOutside={params.hideDatePicker}
                      noPortal
                      showTotalNights={false}
                      enablePastDates={true}
                    />
                  );
                }}
              />
            </FormLabel>
          </div>

          {/* Status */}
          <div className="flex-1 flex flex-col max-w-[180px]">
            <FormLabel text="Status" className="mb-5px text-13px tracking-wider">
              <Multiselect
                options={STATUS_OPTIONS}
                selectedValues={filtersState.status ? [filtersState.status] : []}
                onUpdate={values =>
                  setFilters({ status: (values[0] as 'Draft' | 'Active' | 'Scheduled' | 'Expired') || null })
                }
                placeholderText="All"
                isSingleSelectMode={true}
                isCloseOnSelect={true}
                primaryCtaClassName="bg-ivory"
                itemsClassname="bg-white"
                itemCtaClassName="hover:bg-gray-10"
                hideCheckboxes={true}
              />
            </FormLabel>
          </div>
        </div>

        {/* Row 3 - Action Buttons */}
        <div className="flex justify-between items-center gap-20px w-full">
          <div className="flex items-end gap-10px">
            <FluidButton
              type="secondary"
              onClick={() => {
                handleResetFilters();
              }}
            >
              Reset Filters
            </FluidButton>
          </div>
          <div className="flex items-end gap-10px">
            <FluidButton type="primary" onClick={handleCreateContent}>
              New Content
            </FluidButton>
          </div>
        </div>
      </div>

      {/* Error state */}
      {isError && <ErrorBar />}

      {/* Initial loading state */}
      {!isError && !hasInitialData && <LoadingBar />}

      {/* Table and pagination (only after initial data loads) */}
      {!isError && hasInitialData && (
        <>
          <Table
            columns={tableColumns}
            rows={tableRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={isFetching}
            messageWhenEmpty="No results"
            className="mt-5"
            evenColor="ivory"
          />

          {/* Only show pagination when we have data */}
          {tableRows.length >= 1 && (
            <Pagination
              currentPage={paginationData.page}
              itemsPerPage={paginationData.perPage}
              onItemsPerPageChange={paginationData.setPerPage}
              onPageSelect={paginationData.setPage}
              pageCount={paginationData.pageCount}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        type={EConfirmationModalType.WARNING}
        isOpen={deleteModal.isOpen}
        title="You are going to delete a content item."
        message="Are you sure want to delete this content item?"
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isConfirmLoading={deleteOpportunityMutation.isPending}
      />
    </div>
  );
};
